import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";

import { useAuth } from "/imports/state/AuthContext";

function VerifyEmailView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { completeVerification } = useAuth();

  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    completeVerification(token)
      .then((data) => {
        setStatus("success");
        // Resume an interrupted flow (e.g. a shared booking link) when one was
        // stashed before signup; otherwise drop the user on their club home.
        const resumeTo = localStorage.getItem("widget_v2_post_auth_redirect");
        if (resumeTo) {
          localStorage.removeItem("widget_v2_post_auth_redirect");
          setTimeout(() => navigate(resumeTo, { replace: true }), 1500);
        } else if (data?.slug) {
          setTimeout(() => navigate(`/${data.slug}`, { replace: true }), 1500);
        }
      })
      .catch((err) => {
        setStatus("error");
        setError(err.message || "We couldn't verify your email.");
      });
  }, [token, completeVerification, navigate]);

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" p={3}>
      <Card sx={{ maxWidth: "27.5rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: "center", "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          {status === "verifying" && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={600}>
                Verifying your email...
              </Typography>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircleOutline color="success" sx={{ fontSize: "3.5rem", mb: 1 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Email verified
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You're all set. Taking you to your account...
              </Typography>
            </>
          )}

          {status === "error" && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Verification failed
              </Typography>
              <Alert severity="error" sx={{ mt: 1, textAlign: "left" }}>
                {error}
              </Alert>
              <Typography variant="body2" color="textSecondary" mt={2}>
                The link may have expired or already been used. Please sign in, or ask your club for help.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default VerifyEmailView;
